import { useMutation, useQueryClient } from "react-query";
import { updateClinicianWorkingHours } from "api/clinician";
import { CLINICIAN_WORKING_HOURS } from "@constants/reactQueryKeys";

type WorkingHoursBasicInfo = {
  id?: string | number;
  timezone?: string;
  user_id: string | number;
};

type WorkingHoursMutationVariables = {
  basicInfo: WorkingHoursBasicInfo;
  newWorkingHours: Record<string, unknown>;
};

type AvailabilityMutationOptions = {
  onSuccess?: (data: unknown, variables: WorkingHoursMutationVariables) => void;
  onError?: (error: unknown) => void;
};

/**
 * Custom hook for updating clinician availability.
 *
 * @param {Function} onSuccess Callback function to be called upon successful mutation.
 * @param {Function} onError Callback function to be called upon an error in the mutation.
 * @returns The React Query's useMutation result object containing mutate and isLoading.
 */
export const useUpdateClinicianAvailability = ({
  onSuccess,
  onError,
}: AvailabilityMutationOptions = {}) => {
  const queryClient = useQueryClient();
  const { mutate, mutateAsync, isLoading } = useMutation(
    ({ basicInfo, newWorkingHours }: WorkingHoursMutationVariables) =>
      updateClinicianWorkingHours({
        clinicianId: basicInfo.id,
        clinicianTimeZone: basicInfo.timezone,
        clinicianUserId: basicInfo.user_id,
        newWorkingHours,
      }),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([CLINICIAN_WORKING_HOURS, variables.basicInfo.user_id]);
        onSuccess?.(data, variables);
      },
      onError,
    }
  );
  return { mutate, mutateAsync, isLoading };
};
